import { useEffect, useRef, useState } from 'react';

type SiteHeaderProps = {
  onHome: () => void;
  onMethod: () => void;
  onHouse45: () => void;
  onFaq: () => void;
  onInsights: () => void;
  onAssessment: () => void;
  active?: 'home' | 'method' | 'house45' | 'faq' | 'insights' | 'assessment';
};

const focusableSelector = 'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function SiteHeader({ onHome, onMethod, onHouse45, onFaq, onInsights, onAssessment, active }: SiteHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const run = (action: () => void) => {
    setMenuOpen(false);
    action();
  };

  useEffect(() => {
    if (!menuOpen) return;
    const menu = menuRef.current;
    const first = menu?.querySelector<HTMLElement>(focusableSelector);
    first?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false);
        toggleRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab' || !menu) return;
      const controls = Array.from(menu.querySelectorAll<HTMLElement>(focusableSelector));
      if (!controls.length) return;
      const firstControl = controls[0];
      const lastControl = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === firstControl) {
        event.preventDefault();
        lastControl.focus();
      } else if (!event.shiftKey && document.activeElement === lastControl) {
        event.preventDefault();
        firstControl.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  return (
    <header className={menuOpen ? 'site-header menu-open' : 'site-header'}>
      <button className="site-wordmark" type="button" onClick={() => run(onHome)} aria-label="IMTHEHOUSE home">
        IMTHEHOUSE
      </button>

      <nav className="site-desktop-nav" aria-label="Primary navigation">
        <button className={active === 'method' ? 'active' : ''} type="button" onClick={onMethod}>METHOD</button>
        <button className={active === 'house45' ? 'active' : ''} type="button" onClick={onHouse45}>HOUSE 45</button>
        <button className={active === 'faq' ? 'active' : ''} type="button" onClick={onFaq}>FAQ</button>
        <button className={active === 'insights' ? 'active' : ''} type="button" onClick={onInsights}>INSIGHTS</button>
        <button className="site-header-cta" type="button" onClick={onAssessment}>BEGIN PRIVATE ASSESSMENT</button>
      </nav>

      <button
        ref={toggleRef}
        className="site-menu-toggle"
        type="button"
        aria-controls="site-mobile-menu"
        aria-expanded={menuOpen}
        aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span />
        <span />
      </button>

      <div className="site-mobile-layer" aria-hidden={!menuOpen}>
        <button className="site-menu-backdrop" type="button" tabIndex={menuOpen ? 0 : -1} onClick={() => setMenuOpen(false)} aria-label="Close navigation menu" />
        <nav ref={menuRef} id="site-mobile-menu" className="site-mobile-menu" aria-label="Mobile navigation">
          <span className="site-mobile-menu-label">NAVIGATION</span>
          <button type="button" onClick={() => run(onHome)}>HOME</button>
          <button type="button" onClick={() => run(onMethod)}>METHOD</button>
          <button type="button" onClick={() => run(onHouse45)}>HOUSE 45</button>
          <button type="button" onClick={() => run(onFaq)}>FAQ</button>
          <button type="button" onClick={() => run(onInsights)}>INSIGHTS</button>
          <button className="site-mobile-assessment" type="button" onClick={() => run(onAssessment)}>BEGIN PRIVATE ASSESSMENT</button>
          <p>Private performance coaching.<br />Confidential. Direct. No obligation.</p>
        </nav>
      </div>
    </header>
  );
}
